import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useBoard } from "@/hooks/useBoard";
import KitCapacity from "@/screens/cc/KitCapacity";
import { EventRow } from "@/screens/shared/EventRow";
import { usePipelineBoards } from "./usePipelineBoards";
import { useEventsFeed } from "@/screens/shared/useEventsFeed";
import { WorkGroup } from "@/screens/shared/WorkAxis";
import { ROWS, ACTIVE_STATES } from "@/data/workBoard";
import { Card, Label, MonoMeta, StateWord } from "@/design/primitives";
import { cn } from "@/lib/utils";

// In Flight (v2, Jeff 10/29): replace 4-rail PipelineStrip with three stacked sections
// (In motion / Next up / On hold), fed by pipeline's multi-source rows.
// Capacity strip stays at top. No column rails.

const VISIBLE = 6;

const MONITOR_RANK = { broken: 0, alert: 1, waiting: 2 };
const MONITOR_WORD = {
  broken: "Broken",
  alert: "Alert fired",
  waiting: "Waits on you",
};
const MONITOR_TONE = { broken: "red", alert: "amber", waiting: "amber" };

const MonitorTile = ({ tile }) => {
  const p = tile.payload || {};
  return (
    <div
      data-testid={`inflight-monitor-${tile.slug}`}
      data-state={tile.state}
      className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t border-os-border py-2.5 first:border-t-0"
    >
      <StateWord tone={MONITOR_TONE[tile.state] || "amber"}>
        {MONITOR_WORD[tile.state] || tile.state}
      </StateWord>
      <p className="min-w-0 flex-1 truncate text-[0.875rem] font-medium text-os-ink">
        {p.name || p.title || tile.slug}
      </p>
      <p className="min-w-0 flex-[2] truncate text-[0.8125rem] text-os-ink-3">
        {p.subtitle || p.problem || p.lastRunText || ""}
      </p>
    </div>
  );
};

// Shared inline row (same visual as Desk NextUp)
const InFlightRow = ({ row, board, onFlash, expandedSlug, onToggleExpand }) => {
  const p = row.payload || {};
  const busy = board?.pending === row.slug;
  const stWord = {
    PLAN: "Taking shape",
    TEST: "Being verified",
    INTAKE: "New",
    TABLED: "Parked",
  }[row.state] || row.state;
  const stTone = { PLAN: "amber", TEST: "amber", TABLED: "muted", INTAKE: "muted" }[row.state] || "muted";
  const expanded = expandedSlug === row.slug;
  const isHold = row.state === "TABLED";
  return (
    <div
      data-testid={`inflight-row-${row.slug}`}
      className={cn(
        "flex flex-col border-t border-os-border first:border-t-0",
        expanded && "bg-os-wash/20"
      )}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2.5 px-5">
        <StateWord tone={stTone}>{stWord}</StateWord>
        <button
          type="button"
          onClick={() => onToggleExpand?.(row.slug)}
          title={expanded ? "Collapse" : "Expand"}
          className="min-w-[12rem] max-w-full flex-[2] truncate text-left text-[0.875rem] font-medium text-os-ink underline-offset-2 hover:underline"
        >
          {(p.title || row.slug).replace(/^Next: /, "")}
        </button>
        <p className="hidden min-w-0 flex-[3] truncate text-[0.8125rem] text-os-ink-3 sm:block">
          {(p.blockersLine || "").replace(/^Blockers: /, "").replace(/^Waiting on: /, "") ||
            (isHold ? "Parked â pull one back when you want it worked" : "Waiting for its turn in the bay")}
        </p>
        <div className="ml-auto flex items-center gap-1.5">
          {isHold && (
            <button
              type="button"
              data-testid={`inflight-hold-bringback-${row.slug}`}
              disabled={busy}
              onClick={async () => {
                const res = await board?.act("unDefer", row.slug);
                if (res && onFlash) onFlash("Back on the stack now â it waits for your word again.");
              }}
              className="rounded-control border border-os-border px-2 py-1 font-mono text-[0.6875rem] text-os-ink hover:border-os-border-strong disabled:opacity-40"
            >
              bring back
            </button>
          )}
          <button
            type="button"
            data-testid={`inflight-row-top-${row.slug}`}
            disabled={busy}
            onClick={async () => {
              const ok = await board?.edit("reorder", row.slug, { move: -99 });
              if (ok) onFlash?.("Moved to top.");
            }}
            className="rounded-control border border-os-border px-2 py-1 font-mono text-[0.6875rem] text-os-ink hover:border-os-border-strong disabled:opacity-40"
            title="Move to top"
          >
            â¤
          </button>
          <button
            type="button"
            data-testid={`inflight-row-up-${row.slug}`}
            disabled={busy}
            onClick={async () => {
              const ok = await board?.edit("reorder", row.slug, { move: -1 });
              if (ok) onFlash?.("Moved up.");
            }}
            className="rounded-control border border-os-border px-2 py-1 font-mono text-[0.6875rem] text-os-ink-3 hover:border-os-border-strong hover:text-os-ink disabled:opacity-40"
          >
            â
          </button>
          <button
            type="button"
            data-testid={`inflight-row-down-${row.slug}`}
            disabled={busy}
            onClick={async () => {
              const ok = await board?.edit("reorder", row.slug, { move: 1 });
              if (ok) onFlash?.("Moved down.");
            }}
            className="rounded-control border border-os-border px-2 py-1 font-mono text-[0.6875rem] text-os-ink-3 hover:border-os-border-strong hover:text-os-ink disabled:opacity-40"
          >
            â
          </button>
        </div>
      </div>
      {expanded && (
        <div className="overflow-hidden border-t border-os-border bg-os-wash/40 px-5 pb-5 pt-4 motion-safe:transition-all motion-safe:duration-300">
          {p.goal && <p className="text-[0.8125rem] leading-[1.5] text-os-ink-2">{p.goal}</p>}
          {p.next && (
            <p className="mt-2 text-[0.8125rem] text-os-ink-3">Next: {String(p.next).replace(/^Next: /, "")}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default function InFlightPage() {
  const desktop = useMediaQuery("(min-width: 1024px)");
  const breakage = useBoard("breakage");
  const [openGoalSlug, setOpenGoalSlug] = useState(null);
  const { boards } = usePipelineBoards();
  const projects = boards?.projects;
  const work = boards?.work;
  const events = useEventsFeed();

  const projectRows = useMemo(() => (projects?.items || []), [projects]);
  const activeProjects = useMemo(
    () => projectRows.filter((r) => ["EXECUTE", "TEST", "CLOSE"].includes(r.state)),
    [projectRows],
  );
  const nextUpProjects = useMemo(
    () =>
      projectRows
        .filter((r) => ["PLAN", "TEST", "INTAKE"].includes(r.state))
        .sort((a, b) => (a.sort ?? 900) - (b.sort ?? 900)),
    [projectRows],
  );
  const onHoldProjects = useMemo(
    () =>
      projectRows
        .filter((r) => r.state === "TABLED")
        .sort((a, b) => (a.sort ?? 900) - (b.sort ?? 900)),
    [projectRows],
  );

  const workRows = useMemo(() => (work?.ok ? work.items || [] : ROWS), [work]);
  const groups = useMemo(() => workRows.filter((r) => r.kind === "group"), [workRows]);
  const goalGroups = useMemo(() => {
    const active = groups.filter((g) => ACTIVE_STATES.includes(g.state));
    const dated = groups.filter(
      (g) => g.state === "UNSORTED" && g.slug !== "needs-a-goal",
    );
    return [...active, ...dated];
  }, [groups]);

  const monitors = useMemo(() => {
    const rows = (breakage.items || []).filter(
      (r) =>
        r.kind !== "break-rail" &&
        r.kind !== "break-brand" &&
        ["broken", "alert", "waiting"].includes(r.state),
    );
    return rows.sort(
      (a, b) => (MONITOR_RANK[a.state] ?? 9) - (MONITOR_RANK[b.state] ?? 9),
    );
  }, [breakage.items]);

  const healthyCount = useMemo(() => {
    const rail = (breakage.items || []).find(
      (r) => r.slug === "rail:healthy" || (r.kind === "break-rail" && r.state === "healthy"),
    );
    if (rail) return Number((rail.payload || {}).count) || 0;
    return (breakage.items || []).filter((r) => r.state === "healthy").length;
  }, [breakage.items]);

  const feedRows = useMemo(() => events.slice(0, VISIBLE), [events]);
  const feedHidden = events.length - feedRows.length;

  const [expandedSlug, setExpandedSlug] = useState(null);
  const toggle = (slug) => setExpandedSlug((prev) => (prev === slug ? null : slug));

  const monitorVisible = monitors.slice(0, VISIBLE);
  const monitorHidden = monitors.length - monitorVisible.length;
  const groupsVisible = goalGroups.slice(0, VISIBLE);
  const groupsHidden = goalGroups.length - groupsVisible.length;

  return (
    <div data-testid="inflight-page" className="w-full font-inter text-os-ink">
      <div
        className={cn(
          desktop
            ? "mx-auto flex max-w-[100rem] flex-col gap-4 px-10 py-6"
            : "flex flex-col gap-3 px-4 pb-8 pt-4",
        )}
      >
        {/* Kit capacity strip */}
        <section data-testid="inflight-capacity" className="flex flex-col gap-3">
          <KitCapacity />
        </section>

        {/* 1. In motion */}
        <section data-testid="inflight-in-motion" className="flex flex-col gap-2">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <Label as="h2">In motion</Label>
            <MonoMeta as="p" data-testid="inflight-in-motion-count">
              {activeProjects.length} project{activeProjects.length === 1 ? "" : "s"} active
            </MonoMeta>
          </div>
          <div className="flex flex-col gap-3">
            {activeProjects.length === 0 ? (
              <Card quiet className="px-5 py-4">
                <p className="text-[0.875rem] text-os-ink-2">
                  Nothing is actively running right now â open In Flight or add
                  work from the idea dock.
                </p>
              </Card>
            ) : (
              activeProjects.map((r) => (
                <div key={r.slug} className="border border-os-border rounded px-4 py-3">
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="min-w-0 truncate text-[0.9375rem] font-medium text-os-ink">
                      {(r.payload || {}).title || r.slug}
                    </p>
                    <StateWord
                      tone={r.state === "EXECUTE" ? "green" : "amber"}
                    >
                      {r.state === "EXECUTE" ? "Running" : r.state}
                    </StateWord>
                  </div>
                  <p className="mt-1 text-[0.8125rem] text-os-ink-2">
                    {(r.payload || {}).next || (r.payload || {}).lastKitAction || "—"}
                  </p>
                </div>
              ))
            )}
          </div>
        </section>

        {/* 2. Next up */}
        <section data-testid="inflight-next-up" className="flex flex-col gap-2">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <Label as="h2">Next up</Label>
            <MonoMeta as="p" data-testid="inflight-next-up-count">
              {nextUpProjects.length} project{nextUpProjects.length === 1 ? "" : "s"} in line
            </MonoMeta>
          </div>
          <Card className="px-0 py-0">
            {nextUpProjects.length === 0 ? (
              <p className="px-5 py-4 text-[0.875rem] text-os-ink-2">
                Nothing queued yet â say an idea and it shapes up here.
              </p>
            ) : (
              nextUpProjects.map((row) => (
                <InFlightRow
                  key={row.slug}
                  row={row}
                  board={projects}
                  onFlash={null}
                  expandedSlug={expandedSlug}
                  onToggleExpand={toggle}
                />
              ))
            )}
          </Card>
        </section>

        {/* 3. On hold */}
        <section data-testid="inflight-on-hold" className="flex flex-col gap-2">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <Label as="h2">On hold</Label>
            <MonoMeta as="p" data-testid="inflight-on-hold-count">
              {onHoldProjects.length} parked
            </MonoMeta>
          </div>
          <Card className="px-0 py-0">
            {onHoldProjects.length === 0 ? (
              <p className="px-5 py-4 text-[0.875rem] text-os-ink-2">
                Nothing parked â pull one back when you want it worked.
              </p>
            ) : (
              onHoldProjects.map((row) => (
                <InFlightRow
                  key={row.slug}
                  row={row}
                  board={projects}
                  onFlash={null}
                  expandedSlug={expandedSlug}
                  onToggleExpand={toggle}
                />
              ))
            )}
          </Card>
        </section>

        {/* Monitors */}
        <div className="flex flex-col gap-3">
          <section
            data-testid="inflight-monitors"
            className="flex flex-col gap-3"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <Label as="h2">Monitors</Label>
              <MonoMeta as="p" data-testid="inflight-monitors-healthy">
                {healthyCount} healthy â nothing to read
              </MonoMeta>
            </div>
            <Card as="section" className="flex-1 px-5 py-4">
              {monitors.length === 0 ? (
                <p className="text-[0.875rem] text-os-ink-2">
                  Nothing is broken, firing, or holding for a decision â the watches are quiet.
                </p>
              ) : (
                <div>
                  {monitorVisible.map((tile) => (
                    <MonitorTile key={tile.slug} tile={tile} />
                  ))}
                  {monitorHidden > 0 && (
                    <MonoMeta as="p" className="mt-2">
                      +{monitorHidden} more waiting on the wall
                    </MonoMeta>
                  )}
                </div>
              )}
            </Card>
            <Link
              to="/breakage/board"
              data-testid="inflight-monitors-full"
              className="self-start font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-os-ink-3 underline underline-offset-4 hover:text-os-ink"
            >
              Full wall
            </Link>
          </section>

          {/* Work by goal */}
          <section
            data-testid="inflight-goalaxis"
            className="flex flex-col gap-3"
          >
            <Label as="h2">Work by goal</Label>
            {goalGroups.length === 0 ? (
              <Card quiet className="px-5 py-4">
                <p className="text-[0.875rem] text-os-ink-2">
                  No goals on the board yet â chunks appear as you ask for them.
                </p>
              </Card>
            ) : (
              <div className="flex flex-col gap-4">
                {groupsVisible.map((g) => (
                  <WorkGroup
                    key={g.slug}
                    group={g}
                    tasks={[]}
                    onOpen={(open) => setOpenGoalSlug(open?.slug || open)}
                  />
                ))}
              </div>
            )}
            {groupsHidden > 0 && (
              <MonoMeta as="p" data-testid="inflight-goalaxis-more">
                +{groupsHidden} more goals â open the Work board for the full axis
              </MonoMeta>
            )}
          </section>

          {/* Feed */}
          <section data-testid="inflight-feed" className="flex flex-col gap-2">
            <Label as="h2">The live feed</Label>
            {feedRows.length === 0 ? (
              <Card quiet className="px-4 py-4">
                <p className="text-[0.875rem] text-os-ink-2">
                  No changes recorded yet â this feed carries real state flips only, never filler.
                </p>
              </Card>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {feedRows.map((e) => (
                  <EventRow key={e.id} e={e} testId="inflight-event-row" />
                ))}
              </ul>
            )}
            {feedHidden > 0 && (
              <MonoMeta as="p">+{feedHidden} more changes since the last hour</MonoMeta>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}